import { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasPerm } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Loader2, Search, Plus, Pencil, Trash2, MessageSquarePlus, UserCheck, Megaphone } from "lucide-react";
import { StudentForm } from "@/pages/Students";
import StaffInquiry from "@/pages/StaffInquiry";
import { GraduationCap, Briefcase } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["New", "Contacted", "Visited", "Admitted", "Lost"];
const SOURCES = ["Walk-in", "Phone", "Referral", "Online", "Advertisement", "Other"];
const STATUS_COLOR = {
  New: "bg-blue-100 text-blue-700 border-blue-200",
  Contacted: "bg-amber-100 text-amber-700 border-amber-200",
  Visited: "bg-violet-100 text-violet-700 border-violet-200",
  Admitted: "bg-emerald-100 text-emerald-700 border-emerald-200",
  Lost: "bg-rose-100 text-rose-700 border-rose-200",
};
const EMPTY = { student_name: "", guardian_name: "", phone: "", alt_phone: "", email: "", address: "", class_interested: "", source: "Walk-in", inquiry_date: "", followup_date: "", notes: "", status: "New" };

export default function Leads() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [fStatus, setFStatus] = useState("all");
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [notesTarget, setNotesTarget] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [delTarget, setDelTarget] = useState(null);
  const [convertLead, setConvertLead] = useState(null);
  const [studentFormOpen, setStudentFormOpen] = useState(false);
  const [mode, setMode] = useState("student");

  const canManage = hasPerm(user, "manage_leads");

  const load = useCallback(() => {
    setLoading(true);
    api.get("/leads").then((r) => setRows(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/classes").then((r) => setClasses(r.data)).catch(() => {}); }, []);

  const openAdd = () => setForm({ ...EMPTY, inquiry_date: new Date().toISOString().slice(0, 10) });
  const openEdit = (l) => setForm({ ...EMPTY, ...l });
  const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!form.student_name.trim()) { toast.error("Student name is required"); return; }
    setSaving(true);
    try {
      const payload = {
        student_name: form.student_name, guardian_name: form.guardian_name, phone: form.phone,
        alt_phone: form.alt_phone, email: form.email, address: form.address,
        class_interested: form.class_interested, source: form.source,
        inquiry_date: form.inquiry_date, followup_date: form.followup_date, notes: form.notes, status: form.status,
      };
      if (form.id) await api.put(`/leads/${form.id}`, payload);
      else await api.post("/leads", payload);
      toast.success(form.id ? "Inquiry updated" : "Inquiry added");
      setForm(null); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const changeStatus = async (l, status) => {
    setRows((r) => r.map((x) => (x.id === l.id ? { ...x, status } : x)));
    try { await api.patch(`/leads/${l.id}/status`, { status }); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); load(); }
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    try {
      const r = await api.post(`/leads/${notesTarget.id}/note`, { note: noteText });
      setNotesTarget((p) => ({ ...p, follow_ups: [...(p.follow_ups || []), r.data.entry] }));
      setRows((rows) => rows.map((x) => (x.id === notesTarget.id ? { ...x, follow_ups: [...(x.follow_ups || []), r.data.entry] } : x)));
      setNoteText("");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const doDelete = async () => {
    try { await api.delete(`/leads/${delTarget.id}`); toast.success("Inquiry deleted"); setDelTarget(null); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const startConvert = (l) => { setConvertLead(l); setStudentFormOpen(true); };
  const onStudentAdded = async () => {
    if (convertLead) {
      try { await api.patch(`/leads/${convertLead.id}/status`, { status: "Admitted" }); }
      catch (e) { toast.error("Student created but inquiry status not updated"); }
    }
  };

  const q = search.toLowerCase();
  const filtered = rows.filter((l) => {
    if (fStatus !== "all" && l.status !== fStatus) return false;
    if (q && ![l.student_name, l.guardian_name, l.phone].some((v) => (v || "").toLowerCase().includes(q))) return false;
    return true;
  });
  const counts = STATUSES.reduce((a, s) => ({ ...a, [s]: rows.filter((l) => l.status === s).length }), {});
  const studentInitial = convertLead ? { name: convertLead.student_name, father_name: convertLead.guardian_name, phone: convertLead.phone, alt_phone: convertLead.alt_phone, address: convertLead.address } : null;

  const Switcher = () => (
    <div className="inline-flex rounded-xl border border-slate-200 bg-white p-1 shadow-sm" data-testid="inquiry-mode-switch">
      <button onClick={() => setMode("student")} className={`flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-semibold transition-colors ${mode === "student" ? "bg-indigo-600 text-white" : "text-slate-500 hover:bg-slate-50"}`} data-testid="inquiry-mode-student"><GraduationCap size={16} />Student</button>
      <button onClick={() => setMode("staff")} className={`flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-semibold transition-colors ${mode === "staff" ? "bg-indigo-600 text-white" : "text-slate-500 hover:bg-slate-50"}`} data-testid="inquiry-mode-staff"><Briefcase size={16} />Staff</button>
    </div>
  );

  if (mode === "staff") {
    return (
      <div className="space-y-5">
        <Switcher />
        <StaffInquiry />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <Switcher />
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-extrabold text-slate-900 flex items-center gap-2"><Megaphone className="text-indigo-500" size={26} />Leads / Inquiry</h1>
          <p className="text-slate-500">Capture admission inquiries and move them through your pipeline.</p>
        </div>
        {canManage && <Button className="gap-1.5" onClick={openAdd} data-testid="add-lead-btn"><Plus size={16} />Add Inquiry</Button>}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="lead-stats">
        {STATUSES.map((s) => (
          <button key={s} onClick={() => setFStatus(fStatus === s ? "all" : s)} className={`rounded-xl border p-3 text-left transition-colors ${fStatus === s ? "ring-2 ring-indigo-400" : ""} ${STATUS_COLOR[s]}`} data-testid={`stat-${s}`}>
            <div className="text-2xl font-extrabold tabular">{counts[s] || 0}</div>
            <div className="text-xs font-semibold uppercase tracking-wide">{s}</div>
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-3 flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input className="pl-9 h-9" placeholder="Search name, guardian, phone…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="lead-search" />
        </div>
        <Select value={fStatus} onValueChange={setFStatus}>
          <SelectTrigger className="h-9 w-40" data-testid="lead-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Statuses</SelectItem>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className="bg-white rounded-xl border border-slate-200">
        {loading ? <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-indigo-500" /></div> :
          filtered.length === 0 ? <div className="py-16 text-center text-slate-400"><Megaphone size={40} className="mx-auto mb-2 opacity-40" />No inquiries yet.</div> : (
            <div className="overflow-auto max-h-[calc(100vh-380px)]">
              <table className="w-full text-sm border border-slate-200 border-collapse min-w-[1000px]">
                <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider sticky top-0 z-10">
                  <tr>{["Student", "Phone", "Address", "Class", "Source", "Inquiry", "Follow-up", "Status", "Action"].map((h) => <th key={h} className={`px-3 py-2.5 border border-slate-200 bg-slate-50 ${h === "Action" ? "text-center" : "text-left"}`}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {filtered.map((l) => (
                    <tr key={l.id} className="hover:bg-slate-50" data-testid={`lead-row-${l.id}`}>
                      <td className="px-3 py-2.5 border border-slate-200"><div className="font-medium text-slate-800">{l.student_name}</div>{l.guardian_name && <div className="text-xs text-slate-400">Guardian: {l.guardian_name}</div>}</td>
                      <td className="px-3 py-2.5 border border-slate-200 text-slate-600">{l.phone || "—"}{l.alt_phone ? <div className="text-xs text-slate-400">{l.alt_phone}</div> : null}</td>
                      <td className="px-3 py-2.5 border border-slate-200 text-slate-600 max-w-[200px] truncate" title={l.address}>{l.address || "—"}</td>
                      <td className="px-3 py-2.5 border border-slate-200 text-slate-600">{l.class_interested || "—"}</td>
                      <td className="px-3 py-2.5 border border-slate-200 text-slate-600">{l.source || "—"}</td>
                      <td className="px-3 py-2.5 border border-slate-200 text-slate-500">{l.inquiry_date || "—"}</td>
                      <td className="px-3 py-2.5 border border-slate-200 text-slate-500">{l.followup_date || "—"}</td>
                      <td className="px-3 py-2.5 border border-slate-200">
                        {canManage ? (
                          <Select value={l.status} onValueChange={(v) => changeStatus(l, v)}>
                            <SelectTrigger className={`h-7 text-xs w-32 border ${STATUS_COLOR[l.status] || ""}`} data-testid={`lead-status-${l.id}`}><SelectValue /></SelectTrigger>
                            <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                          </Select>
                        ) : <span className={`px-2 py-0.5 rounded-full text-xs border ${STATUS_COLOR[l.status] || ""}`}>{l.status}</span>}
                      </td>
                      <td className="px-3 py-2.5 border border-slate-200">
                        <div className="flex items-center justify-center gap-1.5">
                          <Button size="sm" variant="ghost" className="h-8 px-2" title="Follow-ups" onClick={() => { setNotesTarget(l); setNoteText(""); }} data-testid={`lead-notes-${l.id}`}><MessageSquarePlus size={15} /></Button>
                          {canManage && <>
                            <Button size="sm" variant="ghost" className="h-8 px-2" title="Edit" onClick={() => openEdit(l)} data-testid={`lead-edit-${l.id}`}><Pencil size={15} /></Button>
                            {l.status !== "Admitted" && <Button size="sm" variant="outline" className="h-8 gap-1 text-emerald-700 border-emerald-200 hover:bg-emerald-50" title="Convert to Student" onClick={() => startConvert(l)} data-testid={`lead-convert-${l.id}`}><UserCheck size={14} />Admit</Button>}
                            <Button size="sm" variant="ghost" className="h-8 px-2 text-rose-600" title="Delete" onClick={() => setDelTarget(l)} data-testid={`lead-delete-${l.id}`}><Trash2 size={15} /></Button>
                          </>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="lead-form-dialog">
          <DialogHeader><DialogTitle>{form?.id ? "Edit Inquiry" : "Add Inquiry"}</DialogTitle><DialogDescription>Admission inquiry details.</DialogDescription></DialogHeader>
          {form && (
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Student Name *</Label><Input value={form.student_name} onChange={(e) => setF("student_name", e.target.value)} data-testid="lead-student-name" /></div>
              <div><Label>Parent / Guardian</Label><Input value={form.guardian_name} onChange={(e) => setF("guardian_name", e.target.value)} data-testid="lead-guardian" /></div>
              <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setF("phone", e.target.value)} data-testid="lead-phone" /></div>
              <div><Label>Alternate Phone</Label><Input value={form.alt_phone} onChange={(e) => setF("alt_phone", e.target.value)} data-testid="lead-alt-phone" /></div>
              <div><Label>Email</Label><Input value={form.email} onChange={(e) => setF("email", e.target.value)} data-testid="lead-email" /></div>
              <div><Label>Class Interested</Label><Input value={form.class_interested} onChange={(e) => setF("class_interested", e.target.value)} data-testid="lead-class" /></div>
              <div className="col-span-2"><Label>Address</Label><Input value={form.address} onChange={(e) => setF("address", e.target.value)} data-testid="lead-address" /></div>
              <div><Label>Source</Label>
                <Select value={form.source} onValueChange={(v) => setF("source", v)}><SelectTrigger data-testid="lead-source"><SelectValue /></SelectTrigger><SelectContent>{SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
              </div>
              <div><Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setF("status", v)}><SelectTrigger data-testid="lead-form-status"><SelectValue /></SelectTrigger><SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
              </div>
              <div><Label>Inquiry Date</Label><Input type="date" value={form.inquiry_date} onChange={(e) => setF("inquiry_date", e.target.value)} data-testid="lead-inquiry-date" /></div>
              <div><Label>Follow-up Date</Label><Input type="date" value={form.followup_date} onChange={(e) => setF("followup_date", e.target.value)} data-testid="lead-followup-date" /></div>
              <div className="col-span-2"><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setF("notes", e.target.value)} data-testid="lead-notes-field" /></div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setForm(null)}>Cancel</Button><Button onClick={save} disabled={saving} data-testid="lead-save">{saving && <Loader2 size={14} className="animate-spin mr-1.5" />}Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!notesTarget} onOpenChange={(o) => !o && setNotesTarget(null)}>
        <DialogContent data-testid="lead-notes-dialog">
          <DialogHeader><DialogTitle>Follow-ups — {notesTarget?.student_name}</DialogTitle><DialogDescription>Log calls and visits for this inquiry.</DialogDescription></DialogHeader>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {(notesTarget?.follow_ups || []).length === 0 ? <p className="text-sm text-slate-400">No follow-ups yet.</p> :
              (notesTarget?.follow_ups || []).map((n, i) => (
                <div key={i} className="text-sm border border-slate-200 rounded-lg px-3 py-2"><div className="text-slate-700">{n.note}</div><div className="text-xs text-slate-400">{n.by} · {(n.at || "").slice(0, 16).replace("T", " ")}</div></div>
              ))}
          </div>
          {canManage && <div className="flex gap-2"><Input placeholder="Add a follow-up note…" value={noteText} onChange={(e) => setNoteText(e.target.value)} data-testid="lead-note-input" /><Button onClick={addNote} data-testid="lead-note-add">Add</Button></div>}
        </DialogContent>
      </Dialog>

      <Dialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <DialogContent data-testid="lead-delete-dialog">
          <DialogHeader><DialogTitle className="text-rose-600">Delete Inquiry?</DialogTitle><DialogDescription>Remove <b>{delTarget?.student_name}</b>'s inquiry. This cannot be undone.</DialogDescription></DialogHeader>
          <DialogFooter><Button variant="outline" onClick={() => setDelTarget(null)}>Cancel</Button><Button className="bg-rose-600 hover:bg-rose-700" onClick={doDelete} data-testid="lead-delete-confirm">Delete</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <StudentForm open={studentFormOpen} onOpenChange={(o) => { setStudentFormOpen(o); if (!o) setConvertLead(null); }} classes={classes} edit={null} initial={studentInitial} addTitle="Admit Inquiry — New Student" onAdded={onStudentAdded} onSaved={load} />
    </div>
  );
}
